/**
 * Display-only: a long message written as one block (no line breaks) is shown in short paragraphs
 * of a couple of sentences, so it is easier to read. The stored text is never changed, and a message
 * the writer already broke into lines is shown exactly as written.
 *
 * Works for Latin scripts (". ! ?" followed by a space and a capital/digit) and Chinese/Japanese
 * ("。！？", no space needed). Decimals like "1.5", "t.ex. kl." inside a sentence and URLs are not split.
 */
const MIN_LENGTH = 220; // weighted characters before we bother
const TARGET = 130; // weighted characters per paragraph (a CJK character counts double): about 2–3 sentences

const weight = (s: string): number => {
  let n = 0;
  for (const ch of s) n += /[⺀-鿿가-힯豈-﫿＀-￯]/.test(ch) ? 2 : 1;
  return n;
};

/** Split points: after .!?… + spaces when the next sentence starts with a capital, digit or quote; after 。！？ (+ closing quote). */
const SENTENCE_END = /(?<=[.!?…])\s+(?=[\p{Lu}\p{N}"'“‘(«])|(?<=[。！？][”’」』）)]?)(?=[^”’」』）)\s])/u;

export function readableParagraphs(text: string): string {
  if (!text || text.includes('\n') || weight(text) < MIN_LENGTH) return text;
  const sentences = text.split(SENTENCE_END).map((s) => s.trim()).filter(Boolean);
  if (sentences.length < 3) return text;
  const paragraphs: string[] = [];
  let current = '';
  for (const s of sentences) {
    const cjkJoin = /[。！？][”’」』）)]?$/.test(current);
    current = current ? current + (cjkJoin ? '' : ' ') + s : s;
    if (weight(current) >= TARGET) {
      paragraphs.push(current);
      current = '';
    }
  }
  if (current) {
    // A very short tail joins the previous paragraph instead of standing alone.
    if (paragraphs.length && weight(current) < TARGET / 3) {
      const last = paragraphs.pop()!;
      paragraphs.push(last + (/[。！？][”’」』）)]?$/.test(last) ? '' : ' ') + current);
    } else paragraphs.push(current);
  }
  return paragraphs.length > 1 ? paragraphs.join('\n\n') : text;
}
